import React, { useContext, useState, useEffect } from "react";
import { ShopContext } from "../context/ShopContext";
import styles from "./CartOverlay.module.scss";
import cardStyles from "../pages/Cart/Cart.module.scss"; // reuse the cart item-card styling
import { IoCloseSharp, IoAdd, IoRemove } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { trackAddToCart } from "../helpers/analytics";
import textures from "../data/textures";

const CartOverlay = ({ isVisible, closeOverlay }) => {
  let navigate = useNavigate();
  const { lastAddedItem, addToCart, updateCartItemCount, products, cartItems } =
    useContext(ShopContext);
  const [lastItemDetails, setLastItemDetails] = useState(null);
  // Locally-selected variation for the colour preview (per card).
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [suggestedSelected, setSuggestedSelected] = useState({});
  // How many were added in THIS action, and how many were already in the
  // basket beforehand — so the overlay shows the just-added amount, not the
  // running cart total.
  const [addedQty, setAddedQty] = useState(1);
  const [baselineQty, setBaselineQty] = useState(0);

  useEffect(() => {
    if (lastAddedItem) {
      setLastItemDetails(
        products.find((product) => product.id === lastAddedItem?.itemId),
      );
      // Reset the preview to the colour that was actually added.
      setLastSelectedIndex(null);
    }
  }, [lastAddedItem, products]);

  // When an item is added, snapshot how many were added in this action and how
  // many were already in the basket beforehand. Intentionally keyed only on
  // lastAddedItem (NOT cartItems) so using the stepper below doesn't re-snapshot.
  useEffect(() => {
    if (!lastAddedItem) return;
    const added = lastAddedItem.quantityToAdd ?? 1;
    const currentTotal =
      cartItems?.[lastAddedItem.itemId]?.[lastAddedItem.variationIndex]
        ?.quantity ?? added;
    setAddedQty(added);
    setBaselineQty(Math.max(0, currentTotal - added));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAddedItem]);

  // Preload every variation's cover photo for the cards shown in the overlay,
  // so switching colour swatches swaps instantly (no first-time load flash).
  useEffect(() => {
    if (!isVisible) return;
    const cards = [lastItemDetails, ...products.slice(2, 5)].filter(Boolean);
    cards.forEach((product) => {
      product.variations?.forEach((variation) => {
        if (variation?.coverPhoto) {
          const img = new Image();
          img.src = variation.coverPhoto;
        }
      });
    });
  }, [isVisible, lastItemDetails, products]);

  // Function to handle closing the overlay
  const handleCloseClick = () => {
    closeOverlay();
  };

  const handleGoToBasket = () => {
    navigate("/cart");
    closeOverlay();
  };

  const handleGoToShop = () => {
    navigate("/shop");
    closeOverlay();
  };

  const handleAddToCart = (suggestedProduct, variationIndex, quantity) => {
    addToCart(suggestedProduct, variationIndex, quantity);
    trackAddToCart(suggestedProduct, variationIndex, quantity);
  };

  const formatPrice = (price) => {
    return Number(price).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const swatchFor = (colour) =>
    colour ? textures[colour.replace(/\s+/g, "").toLowerCase()] : "";

  // Available colours / sizes for a product, shown like the product page's
  // variation swatches. `selectedIndex` highlights the active variation, and
  // clicking a swatch calls `onSelect(i)` to switch the previewed variation.
  const renderVariations = (product, selectedIndex, onSelect) => {
    const swatches = product.variations
      .map((variation, i) => {
        const selected = i === selectedIndex;
        if (variation.colour) {
          const src = swatchFor(variation.colour);
          if (!src) return null;
          return (
            <img
              key={i}
              src={src}
              alt={variation.colour}
              title={variation.colour}
              onClick={() => onSelect && onSelect(i)}
              className={`${styles.overlaySwatch} ${
                selected ? styles.overlaySwatchSelected : ""
              }`}
            />
          );
        }
        if (variation.size) {
          return (
            <span
              key={i}
              onClick={() => onSelect && onSelect(i)}
              className={`${styles.overlaySize} ${
                selected ? styles.overlaySizeSelected : ""
              }`}
            >
              {variation.size}
            </span>
          );
        }
        return null;
      })
      .filter(Boolean);

    if (swatches.length === 0) return null;
    return <div className={styles.overlaySwatches}>{swatches}</div>;
  };

  // --- Last added item ---
  // Drive the quantity and the line price from the cart (single source of
  // truth), so the line price reflects however many were added and updates
  // live when the stepper is used. updateCartItemCount edits the cart without
  // resetting lastAddedItem, so the overlay doesn't re-trigger on +/-.
  const lastItemId = lastAddedItem?.itemId;
  const lastVariationIndex = lastAddedItem?.variationIndex;
  const lastUnitPrice =
    lastItemDetails?.variations?.[lastVariationIndex]?.price ?? 0;
  // Price reflects what's being added in THIS action, not the running total.
  const lastLineTotal = lastUnitPrice * addedQty;
  // Variation shown in the thumbnail (defaults to the one that was added).
  const effectiveLastIndex =
    lastSelectedIndex != null ? lastSelectedIndex : lastVariationIndex;

  // The +/- adjust how many are added in this action; the cart total becomes
  // (what was already there) + (this action's quantity).
  const handleLastPlus = () => {
    if (lastItemId == null || lastVariationIndex == null) return;
    const next = addedQty + 1;
    setAddedQty(next);
    updateCartItemCount(lastItemId, lastVariationIndex, baselineQty + next);
  };

  const handleLastMinus = () => {
    if (lastItemId == null || lastVariationIndex == null) return;
    if (addedQty <= 1) return;
    const next = addedQty - 1;
    setAddedQty(next);
    updateCartItemCount(lastItemId, lastVariationIndex, baselineQty + next);
  };

  return (
    <div className={`${styles.cartOverlay} ${isVisible ? styles.visible : ""}`}>
      <div className={styles.cartContent}>
        <div className={styles.itemAddedTextAndClose}>
          <h2 className={styles.addedToCart}>
            {addedQty} {addedQty === 1 ? "Item" : "Items"} added to Cart
          </h2>
          <div className={styles.overlayCloseButton} onClick={handleCloseClick}>
            <IoCloseSharp />
          </div>
        </div>

        {lastItemDetails && (
          <div className={styles.overlayCard}>
            <Link
              to={`/product/${lastItemDetails.id}`}
              className={styles.overlayThumb}
              onClick={handleCloseClick}
            >
              <img
                key={lastItemDetails.variations[effectiveLastIndex].coverPhoto}
                src={lastItemDetails.variations[effectiveLastIndex].coverPhoto}
                alt={lastItemDetails.name}
              />
            </Link>
            <div className={styles.overlayBody}>
              <Link
                to={`/product/${lastItemDetails.id}`}
                className={styles.overlayTitle}
                onClick={handleCloseClick}
              >
                {lastItemDetails.family
                  ? lastItemDetails.family
                  : lastItemDetails.name}{" "}
                {lastItemDetails.family ? lastItemDetails.size : ""}
              </Link>

              {renderVariations(
                lastItemDetails,
                effectiveLastIndex,
                setLastSelectedIndex,
              )}

              <div className={styles.overlayBottom}>
                <div className={styles.overlayPrice}>
                  {formatPrice(lastLineTotal)} €
                </div>
                <div className={cardStyles.cmStepper}>
                  <span
                    className={cardStyles.cmStepBtn}
                    onClick={handleLastMinus}
                  >
                    <IoRemove />
                  </span>
                  <span className={cardStyles.cmQty}>{addedQty}</span>
                  <span
                    className={cardStyles.cmStepBtn}
                    onClick={handleLastPlus}
                  >
                    <IoAdd />
                  </span>
                </div>
              </div>

              {baselineQty > 0 && (
                <div className={styles.alreadyInBasket}>
                  {baselineQty} already in your basket
                </div>
              )}
            </div>
          </div>
        )}

        <h2 className={styles.youMayLike}>You may also like</h2>
        {products.slice(2, 5).map((suggestedProduct, index) => {
          const selIndex = suggestedSelected[index] ?? 0;
          return (
            <div key={index} className={styles.overlayCard}>
              <Link
                to={`/product/${suggestedProduct.id}`}
                className={styles.overlayThumb}
                onClick={handleCloseClick}
              >
                <img
                  key={suggestedProduct.variations[selIndex].coverPhoto}
                  src={suggestedProduct.variations[selIndex].coverPhoto}
                  alt={suggestedProduct.name}
                />
              </Link>
              <div className={styles.overlayBody}>
                <Link
                  to={`/product/${suggestedProduct.id}`}
                  className={styles.overlayTitle}
                  onClick={handleCloseClick}
                >
                  {suggestedProduct.family
                    ? suggestedProduct.family
                    : suggestedProduct.name}{" "}
                  {suggestedProduct.family ? suggestedProduct.size : ""}
                </Link>

                {renderVariations(suggestedProduct, selIndex, (i) =>
                  setSuggestedSelected((prev) => ({ ...prev, [index]: i })),
                )}

                <div className={styles.overlayBottom}>
                  <div className={styles.overlayPrice}>
                    {formatPrice(suggestedProduct.variations[selIndex].price)} €
                  </div>
                  <button
                    className={styles.basicButtonSmall}
                    style={{ width: "auto", margin: 0, whiteSpace: "nowrap" }}
                    onClick={() =>
                      handleAddToCart(suggestedProduct, selIndex, 1)
                    }
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <button className={styles.basicButton} onClick={handleGoToBasket}>
          View cart and Checkout
        </button>
        <div onClick={handleGoToShop} className={styles.basicUnderlineOnHover}>
          Continue Shopping
        </div>
      </div>
    </div>
  );
};

export default CartOverlay;
